/**
 * THE CALENDAR READS THE CABINET SURFACE OFFERS, AS A TYPED CLIENT.
 *
 *   getAuthorCalendar             GET /v1/calendar              `authors-api.yaml`
 *   getPublishDateSuggestions     GET /v1/calendar/suggestions  `authors-api.yaml`
 *   listCalendarGroupsForAuthors  GET /v1/calendar/groups       `authors-api.yaml` (design 30)
 *
 * ★★ WHY THIS IS A SECOND SEAM AND NOT TWO METHODS ON `CabinetApi`. It was written when
 * `features/cabinet/api/cabinet-api.ts` was another track's file, so the two operations could not
 * be added there. The move onto `CabinetApi` was never made and nothing asks for it now: this seam
 * has its own default (`calendar-api-source.ts`), `CalendarApiProvider` is what the specs override,
 * and the editor's calendar links (`routes/articles/editor/CalendarLinksPanel.tsx`) read through it
 * too. The editor's go-live suggestions go through `EditorApi`'s own `getPublishDateSuggestions`.
 *
 * ★ EVERYTHING ELSE IS BORROWED RATHER THAN RE-BUILT. The types come from the cabinet's generated
 * helpers (`../../cabinet/api` → `OperationResult` / `OperationQuery`, which resolve through
 * `shared/types/generated/authors-api.d.ts`), the refusals are the cabinet's `ApiProblem` /
 * `TransportFailure`, and the screens use the cabinet's `useAsync` and its component kit. Nothing
 * here restates a wire shape: an operation missing from `operations` cannot be named, which is what
 * makes "the operation does not exist" a compile error rather than a runtime 404.
 *
 * ERRORS ARE THROWN, NOT RETURNED — the same contract as `CabinetApi`, so the same `useAsync`
 * catches them and the same `describeProblem` renders them.
 */
import { createContext, useContext } from 'react';
import type { OperationQuery, OperationResult } from '../../cabinet/api';

/** The response of `getAuthorCalendar`: `from`, `to`, `dayLoad`, `events`, `articles`. */
export type AuthorCalendar = OperationResult<'getAuthorCalendar'>;
/** The response of `getPublishDateSuggestions`: `{ items }`. */
export type PublishDateSuggestions = OperationResult<'getPublishDateSuggestions'>;
/** The response of `listCalendarGroupsForAuthors` (design 30): every live event group. */
export type CalendarGroupList = OperationResult<'listCalendarGroupsForAuthors'>;
/** One group - a recurring topic ("Full Moon in Aries", "Mercury Retrograde") - with its kind. */
export type CalendarGroup = CalendarGroupList['items'][number];

/** One sky event on the AUTHOR's wire. Narrower than the admin `CalendarEvent` on purpose — there
 *  is no `source`, no `retired_at` and no `linked_article_count` here. */
export type AuthorCalendarEvent = AuthorCalendar['events'][number];
/** One of this author's own scheduled or published articles inside the range. */
export type AuthorCalendarArticle = AuthorCalendar['articles'][number];
/**
 * How busy a UTC day is ACROSS ALL AUTHORS — a count, and the owner's 2026-08-26 ruling in place of
 * the colleague-by-name week strip the prototype drew (`docs/ui/04-pages.md`). The schema
 * itself says it "can never become a list": no id, no title, no author, no time, no cursor.
 */
export type AuthorCalendarDayLoad = AuthorCalendar['dayLoad'][number];

export interface CalendarApi {
  /**
   * BOTH BOUNDS ARE REQUIRED and the range is capped server-side. A missing bound, an inverted
   * range, an empty range (`from == to`, refused because `to` is exclusive) and a range longer than
   * `calendar_policy.picker_horizon_days` are all 400s
   * (`src/modules/editorial/cabinet/author-calendar.service.ts`, `:90-149`). None of the four
   * carries a domain `error_code` — the registered row answers the generic
   * `REQUEST_VALIDATION_FAILED` — so a screen cannot branch on which one it was and must not try.
   */
  getAuthorCalendar(
    query: OperationQuery<'getAuthorCalendar'>,
    signal?: AbortSignal,
  ): Promise<AuthorCalendar>;

  /**
   * NO RANGE PARAMETERS AT ALL — only `articleId` (accepted and inert) and `limit`. The window is
   * `[server now, server now + picker_horizon_days]`, so this is the one read in the cabinet whose
   * answer moves with the platform's INJECTED clock, which is why the calendar opens on it.
   */
  getPublishDateSuggestions(
    query?: OperationQuery<'getPublishDateSuggestions'>,
    signal?: AbortSignal,
  ): Promise<PublishDateSuggestions>;

  /**
   * The live event groups (design 30), the editor's topics, named in the cabinet's language of the
   * moment - so a caller keys its read on that language and asks again when it changes.
   */
  listCalendarGroups(signal?: AbortSignal): Promise<CalendarGroupList>;
}

const CalendarApiContext = createContext<CalendarApi | null>(null);

export const CalendarApiProvider = CalendarApiContext.Provider;

/**
 * The client in context, or the default one.
 *
 * ★ IT DOES NOT THROW WHEN NOTHING IS PROVIDED, and that is the difference from
 * `useCabinetApi()` (`features/cabinet/api/cabinet-api.ts`). The cabinet's router mounts
 * `CABINET_ROUTES[].Component` with NO props and inside no provider
 * (`web/authors/src/router.tsx`, the `cabinetRoutes` map), so a screen that required a provider
 * would be a white screen the moment it was added to that list. The default is built by
 * ./calendar-api-source, which is fixtures until a build says otherwise — the same default the
 * cabinet's own seam takes.
 */
export function useCalendarApi(): CalendarApi {
  const provided = useContext(CalendarApiContext);
  return provided ?? defaultCalendarApi();
}

/** Assigned by ./calendar-api-source at module load. Held here so this file has no import cycle
 *  with the implementations it types. */
let fallback: (() => CalendarApi) | undefined;

export function setDefaultCalendarApi(factory: () => CalendarApi): void {
  fallback = factory;
}

function defaultCalendarApi(): CalendarApi {
  if (fallback === undefined) {
    throw new Error(
      'No CalendarApi. Import features/calendar/api (the barrel), which installs the default ' +
        'implementation, or wrap the screen in <CalendarApiProvider value={…}>.',
    );
  }
  return fallback();
}
